import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControlLabel,
  IconButton,
  Snackbar,
  Switch,
  Tooltip,
  Typography
} from '@mui/material';
import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import AddIcon from '@mui/icons-material/Add';
import {
  deleteProductModule,
  getAboutTabs,
  getCustomPages,
  getProductModules,
  saveProductModule
} from '../../../services/ecommerceApi';
import type { CustomModule } from '../ECommercePage';
import ContentTagsDialog from './contentTags/ContentTagsDialog';
import ContentTagsStylePreview from './contentTags/ContentTagsStylePreview';
import ContentTagsAlignPreview, {
  normalizeContentTagAlign,
  type ContentTagAlign
} from './contentTags/ContentTagsAlignPreview';
import { getContentTagTemplateLabel, normalizeContentTagVariant } from './contentTags/contentTagTemplates';
import { cleanContentTagsPayload } from './contentTags/contentTagsUtils';
import { buildCustomPageTargetOptions, ensureFavyoflexAdvantageSubpages } from '../utils/favyoflexPanelPages';
import {
  DEFAULT_CONTENT_TAG,
  DEFAULT_CONTENT_TAGS_DIALOG,
  DEFAULT_CONTENT_TAGS_SETTINGS,
  type ContentCardsInsertAfter,
  type ContentCardsPageOption,
  type ContentCardsTargetPage,
  type ContentTagsDialogState,
  type ContentTagsPayload,
  type ContentTagVariant
} from './contentTags/types';
import {
  getCorporatePagePositionOptions,
  resolvePageContentPlacementLabel
} from './contentCards/pageContentPlacements';

const MODULE_TYPE = 'content-tags';
const MODULE_TAB_NAME = 'İçerik Etiketleri';

interface HomePageContentTagsPanelProps {
  companyId: string;
  configId: string;
  homepageModules?: CustomModule[];
  hideGroupedList?: boolean;
  onProvideActions?: (actions: { openEditor: (moduleId?: string | null) => void }) => void;
  onBlocksChanged?: () => void;
}

interface StoredContentTags extends ContentTagsPayload {
  enabled: boolean;
}

interface ContentTagsBlockRecord {
  moduleId: string;
  targetPage: ContentCardsTargetPage;
  displayOrder: number;
  enabled: boolean;
  payload: StoredContentTags;
}

interface SiblingPageBlock {
  moduleId: string;
  targetPage: string;
  title: string;
  kind: 'cards' | 'tags';
}

const TAB_TARGET_CONFIGS = [
  { prefix: 'about-tab', pageType: 'AboutTab', group: 'Hakkımızda' },
  { prefix: 'services-tab', pageType: 'ServiceTab', group: 'Hizmetler' },
  { prefix: 'product-solution-tab', pageType: 'ProductSolutionTab', group: 'Ürün ve Çözümler' },
  { prefix: 'reference-tab', pageType: 'ReferenceTab', group: 'Referanslar' }
] as const;

const HOMEPAGE_OPTION: ContentCardsPageOption = { value: 'homepage', label: 'Ana Sayfa', group: 'Ana Sayfa' };
const CONTACT_PAGE_OPTION: ContentCardsPageOption = {
  value: 'contact',
  label: 'İletişim sayfası',
  group: 'İletişim'
};

const parseStoredContentTags = (settingsJson?: string | null): StoredContentTags | null => {
  if (!settingsJson) return null;
  try {
    const parsed = JSON.parse(settingsJson);
    const payload = cleanContentTagsPayload({
      title: parsed?.title,
      tags: parsed?.tags || [],
      settings: parsed?.settings || DEFAULT_CONTENT_TAGS_SETTINGS,
      position: parsed?.position
    });
    return { ...payload, enabled: parsed?.enabled !== false };
  } catch {
    return null;
  }
};

const tabLabel = (tab: any): string => String(tab?.title || tab?.name || 'İsimsiz Sekme');

/** Soft-delete / gizli sekmeler listeye girmez */
const isTabActive = (tab: any): boolean => tab?.isActive !== false && tab?.IsActive !== false;

/** Yalnızca aktif ana sekmeler ve onların aktif alt sekmeleri (yetim/silinmiş parent altları hariç) */
const buildTabOptions = (tabs: any[], prefix: string, group: string): ContentCardsPageOption[] => {
  const sorted = [...tabs]
    .filter(isTabActive)
    .sort((a, b) => (Number(a?.displayOrder) || 0) - (Number(b?.displayOrder) || 0));
  const mains = sorted.filter((tab) => !tab?.parentId);
  const mainIds = new Set(mains.map((main) => String(main.id)));
  const subsByParent = new Map<string, any[]>();
  sorted
    .filter((tab) => tab?.parentId && mainIds.has(String(tab.parentId)))
    .forEach((tab) => {
      const list = subsByParent.get(String(tab.parentId)) || [];
      list.push(tab);
      subsByParent.set(String(tab.parentId), list);
    });

  const options: ContentCardsPageOption[] = [];
  mains.forEach((main) => {
    options.push({ value: `${prefix}:${main.id}` as ContentCardsTargetPage, label: tabLabel(main), group });
    (subsByParent.get(String(main.id)) || []).forEach((sub) => {
      options.push({
        value: `${prefix}:${sub.id}` as ContentCardsTargetPage,
        label: `${tabLabel(main)} › ${tabLabel(sub)}`,
        group
      });
    });
  });

  return options;
};

const HomePageContentTagsPanel: React.FC<HomePageContentTagsPanelProps> = ({
  companyId,
  configId,
  homepageModules = [],
  hideGroupedList = false,
  onProvideActions,
  onBlocksChanged
}) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [storedBlocks, setStoredBlocks] = useState<ContentTagsBlockRecord[]>([]);
  const [siblingCardBlocks, setSiblingCardBlocks] = useState<SiblingPageBlock[]>([]);
  const [enabled, setEnabled] = useState(false);
  const [editingModuleId, setEditingModuleId] = useState<string | null>(null);
  const [pageTargetOptions, setPageTargetOptions] = useState<ContentCardsPageOption[]>([HOMEPAGE_OPTION]);
  const [dialogState, setDialogState] = useState<ContentTagsDialogState>(DEFAULT_CONTENT_TAGS_DIALOG);
  const [selectedTemplate, setSelectedTemplate] = useState<ContentTagVariant>('solid');
  const [selectedTagAlign, setSelectedTagAlign] = useState<ContentTagAlign>('left');
  const [snack, setSnack] = useState<{ open: boolean; type: 'success' | 'error'; message: string }>({
    open: false,
    type: 'success',
    message: ''
  });

  const positionOptions = useMemo(() => {
    const moduleOptions = homepageModules
      .filter((module) => module.tabName !== MODULE_TAB_NAME)
      .sort((a, b) => (a.order || 0) - (b.order || 0))
      .map((module) => ({ value: module.id, label: `${module.tabName} modülünden sonra` }));

    const cardAfter = siblingCardBlocks
      .filter((block) => block.targetPage === 'homepage')
      .map((block) => ({
        value: block.moduleId,
        label: `"${block.title}" kartlarının altında`
      }));

    const tagAfter = storedBlocks
      .filter(
        (block) =>
          block.targetPage === 'homepage' &&
          block.moduleId !== editingModuleId &&
          block.enabled
      )
      .map((block) => ({
        value: block.moduleId,
        label: `"${block.payload.title || 'Etiket bloğu'}" etiketlerinin altında`
      }));

    return [
      { value: 'categories', label: 'Kategorilerden sonra' },
      ...moduleOptions,
      ...cardAfter,
      ...tagAfter,
      { value: 'end', label: 'Tüm modüllerden sonra (en alta)' }
    ];
  }, [homepageModules, siblingCardBlocks, storedBlocks, editingModuleId]);

  /** Seçili alt sayfadaki kart / diğer etiket bloklarına göre konum seçenekleri */
  const pagePositionOptions = useMemo(() => {
    const targetPage = dialogState.targetPage || 'homepage';
    if (targetPage === 'homepage') return [];

    const options: Array<{ value: string; label: string }> = getCorporatePagePositionOptions(targetPage);

    siblingCardBlocks
      .filter((block) => block.targetPage === targetPage)
      .forEach((block) => {
        options.push({
          value: block.moduleId,
          label: `"${block.title}" kart yapısının altında`
        });
      });

    storedBlocks
      .filter(
        (block) =>
          block.targetPage === targetPage &&
          block.moduleId !== editingModuleId &&
          block.enabled
      )
      .forEach((block) => {
        const title = block.payload.title || `Etiket bloğu (${block.payload.tags.length} etiket)`;
        options.push({
          value: block.moduleId,
          label: `"${title}" etiketlerinin altında`
        });
      });

    return options;
  }, [dialogState.targetPage, siblingCardBlocks, storedBlocks, editingModuleId]);

  const resolveHomepageDisplayOrder = (nextInsertAfter: ContentCardsInsertAfter): number => {
    const sortedModules = [...homepageModules]
      .filter((module) => module.tabName !== MODULE_TAB_NAME)
      .sort((a, b) => (a.order || 0) - (b.order || 0));

    if (nextInsertAfter === 'categories') {
      const firstOrder = sortedModules[0]?.order ?? 0;
      return Math.max(0, firstOrder - 1);
    }
    if (nextInsertAfter === 'end') {
      const lastOrder = sortedModules[sortedModules.length - 1]?.order ?? 0;
      return lastOrder + 1;
    }
    const anchorIndex = sortedModules.findIndex((module) => module.id === nextInsertAfter);
    if (anchorIndex < 0) return 0;
    const anchorOrder = sortedModules[anchorIndex]?.order ?? anchorIndex;
    const nextOrder = sortedModules[anchorIndex + 1]?.order;
    if (typeof nextOrder === 'number') {
      return anchorOrder + (nextOrder - anchorOrder) / 2;
    }
    return anchorOrder + 1;
  };

  const resolveNextHomepageOrderForAnchor = (
    blocks: ContentTagsBlockRecord[],
    anchor: ContentCardsInsertAfter
  ): number => {
    const sameAnchorBlocks = blocks.filter(
      (block) =>
        block.targetPage === 'homepage' && (block.payload.position?.insertAfter || 'end') === anchor
    );
    if (sameAnchorBlocks.length === 0) return resolveHomepageDisplayOrder(anchor);
    return Math.max(...sameAnchorBlocks.map((block) => block.displayOrder ?? 0)) + 1;
  };

  const resolveNextOrderForTarget = (
    blocks: ContentTagsBlockRecord[],
    targetPage: ContentCardsTargetPage
  ): number => {
    const sameTarget = blocks.filter((block) => block.targetPage === targetPage);
    if (sameTarget.length === 0) return 0;
    return Math.max(...sameTarget.map((block) => block.displayOrder ?? 0)) + 1;
  };

  const fetchModuleBlocks = async (): Promise<ContentTagsBlockRecord[]> => {
    const response = await getProductModules(companyId, MODULE_TYPE);
    const rows = Array.isArray(response?.data) ? response.data : [];
    const blocks = rows
      .map((row: any) => {
        const parsed = parseStoredContentTags(row?.settingsJson);
        if (!parsed) return null;
        return {
          moduleId: String(row.id),
          targetPage: parsed.position?.targetPage || 'homepage',
          displayOrder: Number(row.displayOrder) || 0,
          enabled: row.isActive !== false && parsed.enabled !== false,
          payload: parsed
        } satisfies ContentTagsBlockRecord;
      })
      .filter(Boolean) as ContentTagsBlockRecord[];

    blocks.sort((a, b) => a.displayOrder - b.displayOrder);
    return blocks;
  };

  const persistBlockModule = async (
    payload: ContentTagsPayload,
    options: {
      moduleId?: string | null;
      targetPage: ContentCardsTargetPage;
      insertAfter: ContentCardsInsertAfter;
      displayOrder: number;
      isActive: boolean;
    }
  ) => {
    const settingsJson = JSON.stringify({
      enabled: options.isActive,
      title: payload.title,
      tags: payload.tags,
      settings: payload.settings,
      position: { insertAfter: options.insertAfter, targetPage: options.targetPage }
    });

    return saveProductModule({
      id: options.moduleId || undefined,
      companyId,
      configId,
      moduleType: MODULE_TYPE,
      tabName: MODULE_TAB_NAME,
      sectionTitle: payload.title || MODULE_TAB_NAME,
      displayOrder: options.displayOrder,
      isActive: options.isActive,
      settingsJson,
      selectedProductIds: JSON.stringify([]),
      selectedCategoryIds: JSON.stringify([]),
      maxCategories: 0,
      horizontalPosition: 50,
      itemsPerRow: 3
    });
  };

  const loadAll = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      let tabsByConfig: Array<{ prefix: string; pageType: string; group: string; tabs: any[] }> =
        TAB_TARGET_CONFIGS.map((cfg) => ({ ...cfg, tabs: [] as any[] }));
      let customPages: any[] = [];

      if (configId) {
        const tabResponses = await Promise.all(
          TAB_TARGET_CONFIGS.map((cfg) =>
            getAboutTabs(configId, cfg.pageType)
              .then((res: any) => (Array.isArray(res?.data) ? res.data : []))
              .catch(() => [])
          )
        );
        tabsByConfig = TAB_TARGET_CONFIGS.map((cfg, index) => ({ ...cfg, tabs: tabResponses[index] }));

        customPages = await getCustomPages(configId)
          .then((res: any) => (Array.isArray(res?.data) ? res.data : []))
          .catch(() => []);
        customPages = await ensureFavyoflexAdvantageSubpages(configId, companyId, customPages);
      }

      const options: ContentCardsPageOption[] = [
        HOMEPAGE_OPTION,
        ...tabsByConfig.flatMap(({ tabs, prefix, group }) => [
          { value: `${prefix}:main` as ContentCardsTargetPage, label: `${group} sayfası`, group },
          ...buildTabOptions(tabs, prefix, group)
        ]),
        CONTACT_PAGE_OPTION,
        ...buildCustomPageTargetOptions(customPages, companyId).map((option) => ({
          ...option,
          value: option.value as ContentCardsTargetPage
        }))
      ];
      setPageTargetOptions(options);

      const [blocks, cardModulesResponse] = await Promise.all([
        fetchModuleBlocks(),
        getProductModules(companyId, 'content-cards').catch(() => null)
      ]);
      setStoredBlocks(blocks);
      setEnabled(blocks.some((block) => block.enabled));
      if (blocks.length > 0) {
        setSelectedTagAlign(normalizeContentTagAlign(blocks[0].payload.settings.tagAlign));
      }

      const cardRows = Array.isArray(cardModulesResponse?.data) ? cardModulesResponse.data : [];
      const cardSiblings: SiblingPageBlock[] = cardRows
        .map((row: any) => {
          try {
            const parsed = JSON.parse(row?.settingsJson || '{}');
            if (parsed?.enabled === false || row?.isActive === false) return null;
            const cards = Array.isArray(parsed?.cards) ? parsed.cards : [];
            if (cards.length === 0) return null;
            return {
              moduleId: String(row.id),
              targetPage: String(parsed?.position?.targetPage || 'homepage'),
              title: String(parsed?.title || '').trim() || `Kart yapısı (${cards.length} kart)`,
              kind: 'cards' as const
            };
          } catch {
            return null;
          }
        })
        .filter(Boolean) as SiblingPageBlock[];
      setSiblingCardBlocks(cardSiblings);
    } catch (error) {
      console.error('[CONTENT TAGS] Yükleme hatası:', error);
      setSnack({ open: true, type: 'error', message: 'İçerik etiketleri yüklenemedi.' });
    } finally {
      setLoading(false);
    }
  }, [companyId, configId]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const openEditor = (moduleId: string | null = null) => {
    setEditingModuleId(moduleId);

    if (moduleId) {
      const block = storedBlocks.find((item) => item.moduleId === moduleId);
      if (!block) return;

      const base = cleanContentTagsPayload(block.payload);
      setSelectedTemplate(normalizeContentTagVariant(base.settings.variant));
      setSelectedTagAlign(normalizeContentTagAlign(base.settings.tagAlign));
      setDialogState({
        open: true,
        isEdit: true,
        title: base.title || '',
        settings: base.settings,
        tags: base.tags.length > 0 ? base.tags : [{ ...DEFAULT_CONTENT_TAG }],
        insertAfter: base.position?.insertAfter || (block.targetPage === 'homepage' ? 'end' : 'content-end'),
        targetPage: block.targetPage
      });
      return;
    }

    setDialogState({
      open: true,
      isEdit: false,
      title: '',
      settings: {
        ...DEFAULT_CONTENT_TAGS_SETTINGS,
        variant: selectedTemplate,
        tagAlign: selectedTagAlign,
        titleAlign: selectedTagAlign
      },
      tags: [{ ...DEFAULT_CONTENT_TAG }],
      insertAfter: 'end',
      targetPage: 'homepage'
    });
  };

  useEffect(() => {
    onProvideActions?.({ openEditor });
  }, [onProvideActions, storedBlocks]);

  const handleTargetPageChange = (targetPage: ContentCardsTargetPage) => {
    setDialogState((prev) => {
      const wasHomepage = (prev.targetPage || 'homepage') === 'homepage';
      const isHomepage = targetPage === 'homepage';
      if (wasHomepage && isHomepage) return { ...prev, targetPage };
      if (!wasHomepage && !isHomepage) {
        // Alt sayfa değişince önceki bloğa bağlı konum geçersiz kalabilir
        return { ...prev, targetPage, insertAfter: 'content-end' };
      }
      return { ...prev, targetPage, insertAfter: isHomepage ? 'end' : 'content-end' };
    });
  };

  const resolveTargetLabel = (targetPage: ContentCardsTargetPage): string =>
    pageTargetOptions.find((option) => option.value === targetPage)?.label ||
    (targetPage === 'homepage' ? 'Ana Sayfa' : 'Silinmiş sayfa');

  const resolvePositionLabel = (block: ContentTagsBlockRecord): string => {
    const insertAfter = block.payload.position?.insertAfter || 'end';
    if (block.targetPage !== 'homepage') {
      const placementLabel = resolvePageContentPlacementLabel(insertAfter);
      if (placementLabel) return placementLabel;
      const cardSibling = siblingCardBlocks.find((item) => item.moduleId === insertAfter);
      if (cardSibling) return `"${cardSibling.title}" kart yapısının altında`;
      const tagSibling = storedBlocks.find((item) => item.moduleId === insertAfter);
      if (tagSibling) {
        const title =
          tagSibling.payload.title || `Etiket bloğu (${tagSibling.payload.tags.length} etiket)`;
        return `"${title}" etiketlerinin altında`;
      }
      return 'İçeriğin altında';
    }
    return positionOptions.find((option) => option.value === insertAfter)?.label || 'En alta';
  };

  const handleSaveDialog = async () => {
    if (!companyId || !configId) {
      setSnack({ open: true, type: 'error', message: 'Önce e-ticaret yapılandırmasını kaydedin.' });
      return;
    }

    const targetPage = dialogState.targetPage || 'homepage';
    const isHomepage = targetPage === 'homepage';
    const selectedInsertAfter = String(dialogState.insertAfter || '').trim();
    const insertAfter: ContentCardsInsertAfter = isHomepage
      ? dialogState.insertAfter
      : selectedInsertAfter && selectedInsertAfter !== 'content-end'
        ? (selectedInsertAfter as ContentCardsInsertAfter)
        : 'content-end';

    const payload = cleanContentTagsPayload({
      title: dialogState.title,
      tags: dialogState.tags,
      settings: dialogState.settings,
      position: { insertAfter, targetPage }
    });

    if (payload.tags.length === 0) {
      setSnack({ open: true, type: 'error', message: 'En az bir etiketin metnini girmelisiniz.' });
      return;
    }

    const tagWithoutLabel = payload.tags.findIndex((tag) => !String(tag.label || '').trim());
    if (tagWithoutLabel >= 0) {
      setSnack({
        open: true,
        type: 'error',
        message: `Etiket #${tagWithoutLabel + 1} için metin zorunludur.`
      });
      return;
    }

    const existingBlock = editingModuleId
      ? storedBlocks.find((block) => block.moduleId === editingModuleId)
      : null;
    const otherBlocks = editingModuleId
      ? storedBlocks.filter((block) => block.moduleId !== editingModuleId)
      : storedBlocks;

    let nextDisplayOrder: number;
    if (isHomepage) {
      const previousInsertAfter = existingBlock?.payload.position?.insertAfter || 'end';
      const keepOrder =
        existingBlock && existingBlock.targetPage === 'homepage' && previousInsertAfter === insertAfter;
      nextDisplayOrder = keepOrder
        ? existingBlock.displayOrder
        : resolveNextHomepageOrderForAnchor(otherBlocks, insertAfter);
    } else {
      nextDisplayOrder =
        existingBlock && existingBlock.targetPage === targetPage
          ? existingBlock.displayOrder
          : resolveNextOrderForTarget(otherBlocks, targetPage);
    }

    setSaving(true);
    try {
      await persistBlockModule(payload, {
        moduleId: editingModuleId,
        targetPage,
        insertAfter,
        displayOrder: nextDisplayOrder,
        isActive: true
      });

      const isEditing = Boolean(editingModuleId);
      setDialogState(DEFAULT_CONTENT_TAGS_DIALOG);
      setEditingModuleId(null);
      await loadAll();
      onBlocksChanged?.();
      setSnack({
        open: true,
        type: 'success',
        message: isEditing
          ? `Etiket bloğu güncellendi. Sayfa: ${resolveTargetLabel(targetPage)}`
          : `Yeni etiket bloğu eklendi. Sayfa: ${resolveTargetLabel(targetPage)}`
      });
    } catch (error) {
      console.error('[CONTENT TAGS] Kayıt hatası:', error);
      setSnack({ open: true, type: 'error', message: 'İçerik etiketleri kaydedilemedi.' });
    } finally {
      setSaving(false);
    }
  };

  const persistBlocksEnabled = async (blocks: ContentTagsBlockRecord[], checked: boolean) => {
    setSaving(true);
    try {
      for (const block of blocks) {
        const payload = cleanContentTagsPayload(block.payload);
        await persistBlockModule(payload, {
          moduleId: block.moduleId,
          targetPage: block.targetPage,
          insertAfter: block.payload.position?.insertAfter || (block.targetPage === 'homepage' ? 'end' : 'content-end'),
          displayOrder: block.displayOrder,
          isActive: checked
        });
      }
      await loadAll();
      onBlocksChanged?.();
      return true;
    } catch (error) {
      console.error('[CONTENT TAGS] Görünürlük güncelleme hatası:', error);
      setSnack({ open: true, type: 'error', message: 'Etiket görünürlüğü güncellenemedi.' });
      return false;
    } finally {
      setSaving(false);
    }
  };

  const handleAlignChange = async (align: ContentTagAlign) => {
    setSelectedTagAlign(align);
    if (dialogState.open) {
      setDialogState((prev) => ({
        ...prev,
        settings: { ...prev.settings, tagAlign: align, titleAlign: align }
      }));
    }
    if (storedBlocks.length === 0) return;

    setSaving(true);
    try {
      for (const block of storedBlocks) {
        const payload = cleanContentTagsPayload({
          ...block.payload,
          settings: { ...block.payload.settings, tagAlign: align, titleAlign: align }
        });
        await persistBlockModule(payload, {
          moduleId: block.moduleId,
          targetPage: block.targetPage,
          insertAfter:
            block.payload.position?.insertAfter || (block.targetPage === 'homepage' ? 'end' : 'content-end'),
          displayOrder: block.displayOrder,
          isActive: block.enabled
        });
      }
      await loadAll();
      onBlocksChanged?.();
      const label = align === 'center' ? 'orta' : align === 'right' ? 'sağ' : 'sol';
      setSnack({
        open: true,
        type: 'success',
        message: `Etiket konumu ${label} olarak güncellendi.`
      });
    } catch (error) {
      console.error('[CONTENT TAGS] Konum güncelleme hatası:', error);
      setSnack({ open: true, type: 'error', message: 'Etiket konumu güncellenemedi.' });
    } finally {
      setSaving(false);
    }
  };

  const handleEnabledChange = async (checked: boolean) => {
    if (storedBlocks.length === 0 && checked) {
      openEditor(null);
      return;
    }
    if (storedBlocks.length === 0) {
      setEnabled(false);
      return;
    }
    const ok = await persistBlocksEnabled(storedBlocks, checked);
    if (ok) {
      setEnabled(checked);
      setSnack({
        open: true,
        type: 'success',
        message: checked
          ? 'Tüm etiket blokları sitede gösterilecek.'
          : 'Tüm etiket blokları gizlendi. Bloklar silinmedi.'
      });
    }
  };

  const handleToggleBlock = async (block: ContentTagsBlockRecord) => {
    const ok = await persistBlocksEnabled([block], !block.enabled);
    if (ok) {
      setSnack({
        open: true,
        type: 'success',
        message: block.enabled ? 'Etiket bloğu gizlendi.' : 'Etiket bloğu sitede gösterilecek.'
      });
    }
  };

  const handleDeleteBlock = async (moduleId: string) => {
    if (!companyId) return;
    setSaving(true);
    try {
      await deleteProductModule({ id: moduleId, companyId });
      await loadAll();
      onBlocksChanged?.();
      setSnack({ open: true, type: 'success', message: 'Etiket bloğu silindi.' });
    } catch (error) {
      console.error('[CONTENT TAGS] Silme hatası:', error);
      setSnack({ open: true, type: 'error', message: 'Etiket bloğu silinemedi.' });
    } finally {
      setSaving(false);
    }
  };

  const groupedBlocks = useMemo(() => {
    const groups = new Map<string, ContentTagsBlockRecord[]>();
    storedBlocks.forEach((block) => {
      const label = resolveTargetLabel(block.targetPage);
      const list = groups.get(label) || [];
      list.push(block);
      groups.set(label, list);
    });

    return Array.from(groups.entries()).sort(([a], [b]) => {
      if (a === 'Ana Sayfa') return -1;
      if (b === 'Ana Sayfa') return 1;
      return a.localeCompare(b, 'tr');
    });
  }, [storedBlocks, pageTargetOptions, positionOptions]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 2 }}>
        <CircularProgress size={18} />
        <Typography variant="body2" color="text.secondary">
          İçerik etiketleri yükleniyor...
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ mb: 1 }}>
      <Box sx={{ mb: 3, p: 2, borderRadius: 2, bgcolor: '#f8fafc', border: '1px solid #e2e8f0' }}>
        <Typography variant="subtitle1" fontWeight={700} gutterBottom>
          Etiket Stili
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Etiket bloğu için varsayılan stili seçin. Her blok kendi stilini kullanabilir. Etiketler kartlardan
          bağımsızdır; ayrı bir blok olarak eklenir.
        </Typography>
        <ContentTagsStylePreview
          value={selectedTemplate}
          onChange={(variant) => {
            setSelectedTemplate(variant);
            if (dialogState.open) {
              setDialogState((prev) => ({ ...prev, settings: { ...prev.settings, variant } }));
            }
          }}
        />
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1.5, display: 'block' }}>
          Seçili stil: <strong>{getContentTagTemplateLabel(selectedTemplate)}</strong>
        </Typography>

        <Typography variant="subtitle1" fontWeight={700} sx={{ mt: 3 }} gutterBottom>
          Etiket konumu
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Etiketlerin sayfada sola, ortaya veya sağa hizalanmasını seçin. Seçim tüm etiket
          bloklarına ve yeni eklenecek bloklara uygulanır.
        </Typography>
        <ContentTagsAlignPreview
          value={selectedTagAlign}
          onChange={handleAlignChange}
          disabled={saving}
        />
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1.5, display: 'block' }}>
          Seçili konum:{' '}
          <strong>
            {selectedTagAlign === 'center' ? 'Orta' : selectedTagAlign === 'right' ? 'Sağ' : 'Sol'}
          </strong>
        </Typography>
      </Box>

      <FormControlLabel
        control={
          <Switch checked={enabled} onChange={(e) => handleEnabledChange(e.target.checked)} disabled={saving} />
        }
        label="Etiketleri sitede göster"
      />

      <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 1 }}>
        Etiketleri ana sayfaya, Hakkımızda / Hizmetler / Ürün ve Çözümler / Referanslar sekmelerine ve özel
        sayfalara ekleyebilirsiniz. Düğmeyi kapattığınızda bloklar silinmez, yalnızca sitede gizlenir.
      </Typography>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1.5 }}>
        <Button
          variant="contained"
          startIcon={storedBlocks.length > 0 ? <AddIcon /> : <LocalOfferOutlinedIcon />}
          onClick={() => openEditor(null)}
          disabled={saving}
        >
          {storedBlocks.length > 0 ? 'Yeni Etiket Bloğu Ekle' : 'Etiket Bloğu Oluştur'}
        </Button>
      </Box>

      {!hideGroupedList && groupedBlocks.length > 0 && (
        <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
          {groupedBlocks.map(([groupLabel, blocks]) => (
            <Box key={groupLabel}>
              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                {groupLabel}
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {blocks.map((block, index) => {
                  const variantLabel = getContentTagTemplateLabel(
                    normalizeContentTagVariant(block.payload.settings.variant)
                  );
                  return (
                    <Alert
                      key={block.moduleId}
                      severity={block.enabled ? 'info' : 'warning'}
                      icon={block.enabled ? undefined : <VisibilityOffOutlinedIcon fontSize="inherit" />}
                      sx={{ alignItems: 'center' }}
                      action={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Tooltip title={block.enabled ? 'Bloğu gizle' : 'Bloğu göster'}>
                            <Switch
                              size="small"
                              checked={block.enabled}
                              onChange={() => handleToggleBlock(block)}
                              disabled={saving}
                            />
                          </Tooltip>
                          <IconButton size="small" onClick={() => openEditor(block.moduleId)} disabled={saving}>
                            <EditOutlinedIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDeleteBlock(block.moduleId)}
                            disabled={saving}
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      }
                    >
                      <Typography variant="body2" component="span">
                        <strong>Blok {index + 1}:</strong> {block.payload.tags.length} etiket
                        {block.payload.title ? ` · ${block.payload.title}` : ''}
                        {` · Stil: ${variantLabel}`}
                        {` · Konum: ${resolvePositionLabel(block)}`}
                      </Typography>
                      {!block.enabled && (
                        <Chip label="Gizli" size="small" color="warning" sx={{ ml: 1, height: 20 }} />
                      )}
                    </Alert>
                  );
                })}
              </Box>
            </Box>
          ))}
        </Box>
      )}

      <ContentTagsDialog
        state={dialogState}
        onClose={() => {
          setDialogState(DEFAULT_CONTENT_TAGS_DIALOG);
          setEditingModuleId(null);
        }}
        onSave={handleSaveDialog}
        onStateChange={(updater) => setDialogState((prev) => updater(prev))}
        onTargetPageChange={handleTargetPageChange}
        pageTargetOptions={pageTargetOptions}
        positionOptions={positionOptions}
        pagePositionOptions={pagePositionOptions}
      />

      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={() => setSnack((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnack((prev) => ({ ...prev, open: false }))}
          severity={snack.type}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default HomePageContentTagsPanel;
